/**
 * Currency-code normalization, shared by everything in steward-app that has to decide
 * what a stored currency string means.
 *
 * It lives here rather than in the SPA because two sides now depend on agreeing: the
 * SPA FORMATS with it (`src/lib/currency.ts` re-exports this), and
 * `services/snapshot-calculator` LABELS a written snapshot with it (steward-app#484).
 * A second implementation that drifted by one rule would let the calculator write a
 * code the SPA then rejects and silently replaces with its own fallback, which is the
 * class of bug #484 is about.
 */

/**
 * The shape a stored `currency` must have: three ASCII letters, either case, nothing else
 * (no whitespace, no trailing newline). The ONE source for both layers that enforce it:
 * the façade's request schemas (`CURRENCY_CODE_RE`) and the `validation.pattern` on the
 * `currency` field of every strict currency-bearing pack type in `STEWARD_APP_TYPE_DEFS`,
 * which appcore compiles into its attribute check on create and update (steward-app#560).
 *
 * Case-insensitive on purpose (#479 case-folds at render rather than migrating stored
 * data), so a stricter `^[A-Z]{3}$` here would refuse rows the façade accepts. It is a
 * string because a CTYPE field declaration is JSON; `CURRENCY_CODE_RE` is the same text
 * compiled for the façade. Not an ISO 4217 list check: 'XYZ' is well-formed and passes.
 *
 * One known consequence (reading of `services/account-sync/src/sync.ts` `syncType`,
 * 2026-10-06, not pinned by a test): an account, asset or liability write that appcore
 * refuses is fatal to that connection's sync run. That is why the Plaid mapper
 * (`packages/account-aggregation/src/sources/plaidMappers.ts` `providerCurrency`, pinned by
 * `plaidCurrencyGuard.test.ts`) drops a provider code that fails this pattern.
 */
export const CURRENCY_CODE_PATTERN = '^[A-Za-z]{3}$';

/** `CURRENCY_CODE_PATTERN` compiled, for the façade's Zod schemas and hand checks. */
export const CURRENCY_CODE_RE = new RegExp(CURRENCY_CODE_PATTERN);

/**
 * A stored currency string, normalized to what `Intl.NumberFormat` will actually accept,
 * or `undefined` if it is not usable at all.
 *
 * `Intl` takes any WELL-FORMED ISO 4217 code (three letters — an unknown-but-well-formed
 * one like 'XYZ' simply renders as its own code) and throws a RangeError on anything else,
 * during render, which blanks the page into the router's errorElement. Every currency the
 * SPA reads comes back out of schemaless storage: `CurrencyInput` is a free-text field
 * that only caps length, the asset form checks non-empty and not shape, and a snapshot row
 * is whatever the calculator wrote whenever it ran. So "three letters" is not a given, and
 * it is the exact condition `Intl` cares about.
 *
 * Case-folded because grouping is done on the result: a holding entered as 'gbp' and one
 * entered as 'GBP' are the same currency, and counting them apart could hand a statement
 * (or a snapshot) to a currency that is not really dominant.
 */
export function currencyCode(raw: string | undefined): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const code = raw.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : undefined;
}
